/**
 * PATH: packages/ui/src/kanban/components/settings-home/KanbanSettingsPage.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-KANBAN-001
 * PURPOSE: All-Kanban prefs — dock, keyboard, shortcuts, host open mode, outbound links.
 * ROLE IN ARCHITECTURE: Settings-home page. Density default lives on Card face.
 * MAIN EXPORTS: KanbanSettingsPage
 * NON-RESPONSIBILITIES: Does not rebuild org members, labels, or week-start.
 * NOTES FOR FUTURE AI: Host openMode is drawer vs Trello popup, not tile expand.
 */
import type { Dispatch, SetStateAction } from 'react'

import type { KanbanApi } from '../../api'
import { BoardDockCustomizePanel } from '../board-view/BoardDockCustomizePanel'
import { useKanbanDockPrefs } from '../board-view/dockPrefs'
import { useKanbanOpenMode } from '../card-detail/openModePref'
import type { SettingsCapabilities } from './contract'
import { OptionStrip, RowButton, SectionShell } from './chrome'
import { DefaultBoardsSection } from './DefaultBoardsSection'
import { getKanbanSettingsHost, requestKanbanShortcutsOpen } from './kanbanSettingsHost'
import { RelatedSettingsLinks } from './RelatedSettingsLinks'

const OPEN_MODE_OPTIONS = [
  {
    id: 'drawer' as const,
    title: 'Right drawer',
    description: 'Card opens in the host drawer.',
  },
  {
    id: 'popup' as const,
    title: 'Trello popup',
    description: 'Card opens as a centred popup. Not tile expand.',
  },
]

export function KanbanSettingsPage({
  userId,
  api,
  organisationId,
  keyboardMode,
  setKeyboardMode,
  setShortcutsOpen,
  onClose,
  capabilities,
}: {
  userId: string | number | null
  api?: KanbanApi
  organisationId?: number
  keyboardMode: boolean
  setKeyboardMode: (next: boolean) => void
  setShortcutsOpen: Dispatch<SetStateAction<boolean>>
  onClose: () => void
  capabilities?: Pick<SettingsCapabilities, 'hasEmailSettings' | 'hasOrganisationSettings'>
}) {
  const [dockPrefs, setDockPrefs] = useKanbanDockPrefs(userId)
  const [openMode, setOpenMode] = useKanbanOpenMode(userId)
  const host = getKanbanSettingsHost()
  const defaultsApi = api ?? host?.api
  const defaultsOrg = organisationId ?? host?.organisationId

  return (
    <div data-testid="settings-kanban-page">
      <DefaultBoardsSection api={defaultsApi} organisationId={defaultsOrg} />
      <SectionShell
        title="Open a card"
        hint="Host shell. Separate from Card face hide/show, which changes the tile."
      >
        <OptionStrip
          label="Open as"
          value={openMode}
          options={OPEN_MODE_OPTIONS}
          testId="settings-open-mode"
          onChange={setOpenMode}
        />
      </SectionShell>

      <SectionShell
        title="Dock & bottom bar"
        hint="Choose which launchers show in the floating dock, or switch back to the classic bar."
      >
        <BoardDockCustomizePanel prefs={dockPrefs} setPrefs={setDockPrefs} />
      </SectionShell>

      <SectionShell title="Keyboard" hint="Navigation mode is on or off. Shortcuts follow you across devices.">
        <label className="flex items-center justify-between gap-3 py-1 text-xs text-neutral-600 dark:text-zinc-300">
          Keyboard navigation mode
          <button
            type="button"
            role="switch"
            data-testid="settings-keyboard-mode"
            aria-checked={keyboardMode}
            onClick={() => setKeyboardMode(!keyboardMode)}
            className={[
              'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
              keyboardMode ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-zinc-700',
            ].join(' ')}
          >
            <span
              className={[
                'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                keyboardMode ? 'translate-x-4' : 'translate-x-0.5',
              ].join(' ')}
            />
          </button>
        </label>
        <RowButton
          label="View & customise keyboard shortcuts…"
          testId="board-settings-open-shortcuts"
          onClick={() => {
            onClose()
            setShortcutsOpen(true)
            requestKanbanShortcutsOpen()
          }}
        />
      </SectionShell>

      <SectionShell
        title="Organisation and email"
        hint="These are not Kanban settings. Link out. Do not rebuild members or week-start."
      >
        <RelatedSettingsLinks capabilities={capabilities} />
      </SectionShell>
    </div>
  )
}
